import React, { useEffect, useRef } from 'react';
import { Toaster } from 'react-hot-toast';
import useStore from '@store';
import { listAPI, ListWebSocket } from '@api/client';
import Header from '@components/Header';
import Editor from '@components/Editor';
import LockDialog from '@components/LockDialog';
import PasswordDialog from '@components/PasswordDialog';
import ShareDialog from '@components/ShareDialog';
import toast from 'react-hot-toast';
import SetPasswordDialog from '@components/SetPasswordDialog';

function App() {
  const wsRef = useRef(null);
  const {
    listId,
    initializeList,
    setListData,
    setContent,
    setConnected,
    setCollaborators,
    setError,
    reset,
  } = useStore();
  
  // Initialize list on mount
  useEffect(() => {
    const initList = async () => {
      // Get path from URL
      const path = window.location.pathname;
      
      // Handle cancel page - redirect to backend
      if (path.startsWith('/cancel/')) {
        window.location.href = `https://api.letsnote.app${path}${window.location.search}`;
        return;
      }
      
      // Get list ID from URL or generate new one
      const pathId = path.slice(1);
      const id = initializeList(pathId || null);
      
      try {
        // Fetch list data
        const data = await listAPI.getList(id);
        setListData(data);
        
        // Check for stored password
        const storedPassword = sessionStorage.getItem(`password_${id}`);
        if (storedPassword && data.isProtected) {
          // Verify stored password
          try {
            await listAPI.verifyPassword(id, storedPassword);
            useStore.setState({ 
              password: storedPassword,
              isOwner: true 
            });
          } catch {
            // Invalid stored password, clear it
            sessionStorage.removeItem(`password_${id}`);
          }
        }
        
        // Initialize WebSocket connection
        wsRef.current = new ListWebSocket(
          id,
          handleWebSocketMessage,
          handleWebSocketStatus
        );
        wsRef.current.connect();
        
      } catch (error) {
        console.error('Failed to initialize list:', error);
        setError('Failed to load list');
      }
    };
    
    initList();
    
    // Cleanup on unmount
    return () => {
      if (wsRef.current) {
        wsRef.current.disconnect();
      }
      reset();
    };
  }, []);
  
  
  // Handle WebSocket messages
  const handleWebSocketMessage = (data) => {
    console.log('WebSocket message received:', data.type, data);
    
    switch (data.type) {
      case 'connected':
        console.log('WebSocket connected with session:', data.sessionId);
        break;
        
      case 'content-update':
        console.log('Content update received:', data.content);
        // Force update the content in store and editor
        if (data.content !== useStore.getState().content) {
          setContent(data.content);
          // Also trigger editor update if needed
          console.log('Content updated in store');
        }
        break;
        
      case 'collaborators':
        setCollaborators(data.count);
        break;
        
      case 'list-locked':
        useStore.setState({ 
          isProtected: true,
          hasPassword: true 
        });
        break;
        
      default:
        console.log('Unknown WebSocket message:', data);
    }
  };
  
  // Handle WebSocket connection status
  const handleWebSocketStatus = ({ connected, error }) => {
    setConnected(connected);
    if (error) {
      console.error('WebSocket error');
    }
  };
  
  // Send content updates via WebSocket
  useEffect(() => {
    const unsubscribe = useStore.subscribe(
      (state) => state.content,
      (content) => {
        if (wsRef.current && content) {
          wsRef.current.send({
            type: 'content-update',
            content,
          });
        }
      }
    );
    
    return unsubscribe;
  }, []);
  
  // Handle Stripe success redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId && listId) {
      // Confirm the upgrade with backend
      listAPI.confirmLock(listId, sessionId)
        .then((data) => {
          useStore.setState({
            tier: 'paid',
            isProtected: true,
            isOwner: true,
            expiresAt: null, // Remove expiration
          });
          
          // Clean URL
          window.history.replaceState({}, '', `/${listId}`);
          
          // Show success message
          toast.success('List upgraded! It will now be saved forever.');
        })
        .catch((error) => {
          console.error('Failed to confirm upgrade:', error);
          toast.error('Failed to confirm payment');
        });
    }
  }, [listId]);
  
  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f8fafc', 
      color: '#1e293b' 
    }}>
      <Header />
      
      <main>
        <Editor />
      </main>
      
      {/* Dialogs */}
      <LockDialog />
      <PasswordDialog />
      <ShareDialog />
      <SetPasswordDialog />
      
      {/* Toast notifications */}
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: '#ffffff',
            color: '#1e293b',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          },
          duration: 3000,
        }}
      />
    </div>
  );
}

export default App;